import { useEffect, useRef, useState } from 'react'

import {
    Box,
    Button,
    Card,
    Checkbox,
    Container,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useBoolean,
    useMediaQuery,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import * as Yup from 'yup'

import {
    useGetExternalCodeMutation,
    useGetRegisterQueryQuery,
    useLoginMutation,
    useLoginViaServiceMutation,
} from '@core/app/api/authApi'
import { useGetThemeQuery } from '@core/app/api/globalApi'
import { useGetHeaderInfoQuery } from '@core/app/api/profileApi'
import { PasswordEyeIcon } from '@core/assets/icons/password_eye'
import { PasswordEyeIconSlash } from '@core/assets/icons/password_eye_slash'
import facebookIcon from '@core/assets/icons/socials/facebook.svg'
import googleIcon from '@core/assets/icons/socials/google.svg'
import linkedinIcon from '@core/assets/icons/socials/linkedin.svg'
import microsoftIcon from '@core/assets/icons/socials/microsoft.svg'
import nobankIcon from '@core/assets/icons/socials/nobank.svg'
import sebankIcon from '@core/assets/icons/socials/sebank.svg'
import vippsIcon from '@core/assets/icons/socials/vipps.svg'
import bg from '@core/assets/images/register_bg.jpg'
import { AppButton } from '@core/components/AppButton'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import { Header } from '@core/components/Header/Header'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import i18n from '@core/utils/i18n'

import { PhoneVerificationStep } from './components/PhoneVerification'

interface FormValues {
    email: string
    password: string
    remember_me: boolean
}

export const LoginPageContent = () => {
    const [login, { isLoading }] = useLoginMutation()
    const [loginViaService] = useLoginViaServiceMutation()
    const [getCode] = useGetExternalCodeMutation()
    const { data: registerParams } = useGetRegisterQueryQuery({})
    const { appTheme, updateTheme, isExpired } = useAppTheme()
    const { data: theme } = useGetThemeQuery({}, { skip: isExpired })

    const [searchParams] = useSearchParams()
    const emailParam = searchParams.get('email')

    const initialValues: FormValues = {
        email: emailParam || '',
        password: '',
        remember_me: false,
    }

    const [showPassword, setShowPassword] = useBoolean()
    const [showVerification, setShowVerification] = useBoolean(false)
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [auth, setAuth] = useState<{ email: string; password: string }>()
    const [err, setErr] = useState({ userNotFound: '' })
    const loc = useLocation()
    const { executeRecaptcha } = useGoogleReCaptcha()
    const [bgImage, setBgImage] = useState<string>('')
    const [bgColor, setBgColor] = useState<string>('')
    const { data: headerInfo } = useGetHeaderInfoQuery({})
    const isSignUpAvailable = headerInfo && headerInfo.statusCode === 200 && headerInfo?.menu?.signUp
    const { t } = useTranslation()
    const [isMobile] = useMediaQuery('(max-width: 480px)', {
        fallback: false,
    })

    const loginHandler = async (
        values: FormValues,
        submitted: Function,
        setStatus: Function,
        setFieldError: Function
    ) => {
        if (!executeRecaptcha) {
            return
        }

        const token = await executeRecaptcha('api_login')

        if (token) {
            try {
                const response = await login({
                    data: {
                        email: values.email,
                        password: values.password,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                if (response.isMFA) {
                    setAuth({ email: values.email, password: values.password })
                    setPhoneNumber(response.phone)
                    setShowVerification.on()
                } else {
                    localStorage.removeItem('apply_dialog')
                    localStorage.setItem('is_logged', 'true')
                    location.href = response.redirectUrl
                }
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage =
                        error?.data?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setStatus({ smthWrong: apiMessage })
                } else if (error?.data?.errors?.email) {
                    setFieldError('email', error?.data?.errors?.email[0] || 'An unexpected error occurred.')
                } else {
                    setStatus({
                        smthWrong: error?.data?.errors?.__errors[0] || 'An unexpected error occurred.',
                    })
                }
            } finally {
                removeLocalStorageItem('feedbackTop')
                submitted()
            }
        }
    }

    const getCodeHandler = async (serviceId: number) => {
        try {
            const response = await getCode({
                data: {
                    typeId: 3,
                    serviceId,
                },
            }).unwrap()

            location.href = response.link
        } catch (error) {
            console.error('getCodeHandler', error)
        }
    }

    const loginViaServiceHandler = async (code: string) => {
        try {
            const response = await loginViaService({ code }).unwrap()

            if (response.isMFA) {
                const url = new URL(window.location.href)
                url.searchParams.delete('code')
                history.replaceState(null, '', url.href)

                setAuth({ email: '', password: '' })
                setPhoneNumber(response.phone)
                setShowVerification.on()
            } else {
                location.href = response.redirectUrl
                localStorage.setItem('is_logged', 'true')
            }
        } catch (error: any) {
            console.error('loginViaServiceHandler', error)
            setErr({
                userNotFound: error.data?.message || 'An unexpected error occurred.',
            })
        }
    }

    useEffect(() => {
        removeLocalStorageItem('feedbackTop')
    }, [])

    useEffect(() => {
        const codeParam = searchParams.get('code')
        if (codeParam) {
            loginViaServiceHandler(loc.search)
        }
    }, [loc, searchParams])

    useEffect(() => {
        if (theme) {
            updateTheme(theme)
        }
    }, [theme])

    useEffect(() => {
        if (!appTheme) return
        const newSettings = appTheme.newSettings?.general
        if (newSettings) {
            if (newSettings.BackgroundUrl) {
                setBgImage(newSettings.BackgroundUrl)
            } else {
                setBgImage(bg)
            }
        } else {
            setBgImage(bg)
        }
    }, [appTheme])

    return (
        <Flex
            // bgImage={bgImage && bgImage}
            bgImage={appTheme?.newSettings?.general?.BackgroundUrl ?? bg}
            bgColor={bgColor && bgColor}
            backgroundSize={'cover'}
            flexDirection={'column'}
            minH={'100vh'}
        >
            <Header />
            <Box as={'section'} flexGrow={1} display={'flex'} alignItems={'center'}>
                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }} h={'full'}>
                    <Flex justifyContent={'end'} alignItems={'center'} h={'full'} py={{ base: 5, xl: 0 }}>
                        <Card
                            w={{ base: '100%', xl: '440px' }}
                            bg={'text.white'}
                            py={{ base: '28px', xl: '44px' }}
                            px={{ base: '32px', xl: '48px' }}
                            my={{ base: 0, xl: '32px' }}
                            mx={{ base: 0, xl: '32px' }}
                        >
                            {showVerification ? (
                                <PhoneVerificationStep
                                    authData={auth}
                                    phone={phoneNumber}
                                    onError={() => setShowVerification.off()}
                                />
                            ) : (
                                <>
                                    <Text
                                        fontSize={'2xl'}
                                        fontWeight={'semibold'}
                                        color={'text.main'}
                                        mb={3}
                                        textTransform={'capitalize'}
                                    >
                                        {t('1278')}
                                    </Text>
                                    {isSignUpAvailable ? (
                                        <Text fontSize={'sm'} color={'text.secondary'} mb={6}>
                                            {t('6583')}?{' '}
                                            <Button
                                                variant={'link'}
                                                fontSize={'sm'}
                                                fontWeight={'medium'}
                                                color={'text.link'}
                                                onClick={() => (location.href = '/sign-up')}
                                            >
                                                {t('6584')}
                                            </Button>
                                        </Text>
                                    ) : null}
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={Yup.object({
                                            email: Yup.string()
                                                .email(`${t('6456')}`)
                                                .required(`${t('6456')}`),
                                            password: Yup.string().required(`${t('6599')}`),
                                        })}
                                        onSubmit={(values, { setSubmitting, setStatus, setFieldError }) => {
                                            const submitted = () => {
                                                setSubmitting(false)
                                            }
                                            loginHandler(values, submitted, setStatus, setFieldError)
                                        }}
                                    >
                                        {({
                                            values,
                                            touched,
                                            errors,
                                            handleSubmit,
                                            handleBlur,
                                            handleChange,
                                            setFieldValue,
                                            setFieldError,
                                            status,
                                            setStatus,
                                        }) => (
                                            <Form onSubmit={handleSubmit}>
                                                <FormControl
                                                    isInvalid={
                                                        (!!errors.email && touched.email) ||
                                                        (status && status.smthWrong)
                                                    }
                                                    mb={4}
                                                >
                                                    <FormLabel>
                                                        <Text fontSize={'xs'}>{t('92')}*</Text>
                                                    </FormLabel>
                                                    <Input
                                                        type={'text'}
                                                        name={'email'}
                                                        value={values.email}
                                                        onChange={(e) => {
                                                            handleChange(e)
                                                            setStatus({
                                                                smthWrong: '',
                                                            })
                                                            setFieldError('email', '')
                                                        }}
                                                        onBlur={handleBlur}
                                                        placeholder={`${t('727')}`}
                                                        transition={'all 0.2s ease-in-out'}
                                                        _hover={{
                                                            backgroundColor: '#f9f9f9',
                                                        }}
                                                        _focus={{
                                                            boxShadow: '0 0 0 1px #E0E0e0',
                                                        }}
                                                        cursor={'pointer'}
                                                    />
                                                    <FormErrorMessage>
                                                        {errors.email && touched.email && errors.email}
                                                    </FormErrorMessage>
                                                </FormControl>
                                                <FormControl
                                                    isInvalid={
                                                        (!!errors.password && touched.password) ||
                                                        (status && status.smthWrong)
                                                    }
                                                >
                                                    <FormLabel>
                                                        <Text fontSize={'xs'}>{t('423')}*</Text>
                                                    </FormLabel>
                                                    <InputGroup>
                                                        <Input
                                                            type={showPassword ? 'text' : 'password'}
                                                            name={'password'}
                                                            value={values.password}
                                                            onChange={(e) => {
                                                                handleChange(e)
                                                                setStatus({
                                                                    smthWrong: '',
                                                                })
                                                            }}
                                                            onBlur={handleBlur}
                                                            placeholder={`${t('699')}`}
                                                            transition={'all 0.2s ease-in-out'}
                                                            _hover={{
                                                                backgroundColor: '#f9f9f9',
                                                            }}
                                                            _focus={{
                                                                boxShadow: '0 0 0 1px #E0E0e0',
                                                            }}
                                                            cursor={'pointer'}
                                                        />
                                                        <InputRightElement>
                                                            <Button
                                                                variant={'unstyled'}
                                                                onClick={setShowPassword.toggle}
                                                                aria-label={
                                                                    showPassword ? 'Show password' : 'Hide password'
                                                                }
                                                            >
                                                                {showPassword ? (
                                                                    <PasswordEyeIconSlash width={4} height={4} />
                                                                ) : (
                                                                    <PasswordEyeIcon width={4} height={4} />
                                                                )}
                                                            </Button>
                                                        </InputRightElement>
                                                    </InputGroup>
                                                    <FormErrorMessage>
                                                        {errors.password && touched.password && errors.password}
                                                    </FormErrorMessage>
                                                </FormControl>
                                                {status && status.smthWrong && (
                                                    <Text fontSize={'xs'} color={'text.error'} mt={1}>
                                                        {status.smthWrong}
                                                    </Text>
                                                )}
                                                {err && err.userNotFound && (
                                                    <Text fontSize={'xs'} color={'text.error'} mt={1}>
                                                        {err.userNotFound}
                                                    </Text>
                                                )}
                                                <HStack justifyContent={'space-between'} mt={4}>
                                                    <FormControl maxW={'50%'}>
                                                        <Checkbox
                                                            checked={values.remember_me}
                                                            onChange={(e) =>
                                                                setFieldValue('remember_me', e.target.checked)
                                                            }
                                                            _hover={{
                                                                opacity: '0.8',
                                                            }}
                                                        >
                                                            <Text
                                                                fontSize={'sm'}
                                                                fontWeight={'medium'}
                                                                color={'text.secondary'}
                                                            >
                                                                {t('6585')}
                                                            </Text>
                                                        </Checkbox>
                                                    </FormControl>
                                                    <Button
                                                        fontSize={'sm'}
                                                        fontWeight={'normal'}
                                                        color={'text.link'}
                                                        variant={'link'}
                                                        onClick={() => (location.href = '/forgot-password')}
                                                    >
                                                        {t('6586')}?
                                                    </Button>
                                                </HStack>
                                                <Flex>
                                                    <FormControl display={'flex'} justifyContent={'end'} mt={6}>
                                                        <AppButton
                                                            translate="no"
                                                            title={t('1890')}
                                                            type={'submit'}
                                                            variant={'primary'}
                                                            w={'120px'}
                                                            isLoading={isLoading}
                                                        />
                                                    </FormControl>
                                                </Flex>
                                            </Form>
                                        )}
                                    </Formik>
                                    <Box mt={6}>
                                        {(registerParams?.settings?.google ||
                                            registerParams?.settings?.microsoft ||
                                            registerParams?.settings?.facebook ||
                                            registerParams?.settings?.linkedin ||
                                            registerParams?.settings?.vipps) && (
                                            <HStack mb={6}>
                                                <Divider />
                                                <Text fontSize={'xs'} color={'text.thirdly'} flex={'none'}>
                                                    {t('6474')}
                                                </Text>
                                                <Divider />
                                            </HStack>
                                        )}
                                        <Flex
                                            gap={isMobile ? 4 : 5}
                                            alignItems={'center'}
                                            flexWrap={'wrap'}
                                            justifyContent="center"
                                        >
                                            {registerParams?.settings?.google && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'Google',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(2)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image src={googleIcon} w={isMobile ? 6 : 8} alt="Google" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                            {registerParams?.settings?.microsoft && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'Microsoft',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(1)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image
                                                            src={microsoftIcon}
                                                            w={isMobile ? 6 : 8}
                                                            alt="Microsoft"
                                                        />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                            {registerParams?.settings?.facebook && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'Facebook',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(10)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image src={facebookIcon} w={isMobile ? 6 : 8} alt="Facebook" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                            {registerParams?.settings?.linkedin && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'LinkedIn',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(11)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image src={linkedinIcon} w={isMobile ? 6 : 8} alt="LinkedIn" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}

                                            {registerParams?.settings?.vipps && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'Vipps',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(9)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image src={vippsIcon} w={isMobile ? 6 : 8} alt="Vipps" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                            {registerParams?.settings?.nobankid && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'BankID Norway',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(4)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    >
                                                        <Image src={nobankIcon} w={isMobile ? 6 : 8} alt="NoBank" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                            {registerParams?.settings?.sebankid && (
                                                <ApplyV2Tooltip
                                                    label={t('7512', 'Apply/sign up with {{social}}', {
                                                        social: 'BankID Sweden',
                                                    })}
                                                >
                                                    <Box
                                                        as={'button'}
                                                        onClick={() => getCodeHandler(5)}
                                                        cursor={'pointer'}
                                                        borderRadius={'4px'}
                                                        overflow={'hidden'}
                                                        transition={'all .2s ease-in-out'}
                                                        transform={'scale(1.1)'}
                                                        _hover={{
                                                            opacity: '0.8',
                                                            transform: 'scale(1.2)',
                                                        }}
                                                    >
                                                        <Image src={sebankIcon} w={isMobile ? 6 : 8} alt="SeBank" />
                                                    </Box>
                                                </ApplyV2Tooltip>
                                            )}
                                        </Flex>
                                    </Box>
                                    {/* {appTheme?.customerPortalLink ? (
                                        <Box mt={8} display={'flex'} justifyContent={'center'}>
                                            <Text fontSize={'sm'} fontWeight={'medium'} color={'text.secondary'} flex={'none'}>
                                                <a
                                                    href={appTheme.customerPortalLink}
                                                    style={{
                                                        color: 'inherit',
                                                        textDecoration: 'none',
                                                        padding: '8px 86px',
                                                        // border: '2px solid transparent',
                                                        borderRadius: '50px',
                                                        transition: 'all 0.3s',
                                                        display: 'inline-block',
                                                        backgroundColor: 'transparent',
                                                        textAlign: 'center'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.borderColor = '#000';
                                                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.borderColor = 'transparent';
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    {t('7948')}
                                                </a>
                                            </Text>
                                        </Box>
                                    ) : null} */}
                                </>
                            )}
                        </Card>
                    </Flex>
                </Container>
            </Box>

            <Box as={'footer'} py={{ base: 8, xl: 6 }} bg={{ base: 'rgba(0, 0, 0, 0.5)' }}>
                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }}>
                    <Flex justifyContent={{ base: 'center', xl: 'end' }}>
                        {appTheme?.customerPortalLink ? (
                            <Box
                                as={'a'}
                                href={appTheme.customerPortalLink}
                                fontSize={'xs'}
                                color={'text.white'}
                                cursor={'pointer'}
                                _hover={{
                                    textDecoration: 'underline',
                                }}
                                mr={4}
                            >
                                {t('7948')}
                            </Box>
                        ) : null}

                        <Text color={'text.white'} fontSize={'xs'}>
                            {t('6587')} -{' '}
                            <Box as={'a'} href={'/privacy-policy'} color={'text.link'}>
                                {t('5034')}
                            </Box>
                        </Text>
                    </Flex>
                </Container>
            </Box>
        </Flex>
    )
}

export const LoginPage = () => {
    return (
        // <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA} language={i18n.language} useEnterprise={true}>
        <LoginPageContent />
        // </GoogleReCaptchaProvider>
    )
}
